import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';

interface Fan {
  id: number;
  wx_account_id: number;
  openid: string;
  nickname?: string;
  notify_channel?: 'wechat' | 'apple' | 'android';
  subscribed?: boolean;
  last_active_at?: string;
}

const CHANNELS: Fan['notify_channel'][] = ['wechat', 'apple', 'android'];

export default function FansPage() {
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const { data, isLoading, error } = useQuery<{ items: Fan[]; total: number }>({
    queryKey: ['fans', search],
    queryFn: () => api.get(`/api/fans?search=${encodeURIComponent(search)}`),
  });

  const setChannel = useMutation({
    mutationFn: ({ id, channel }: { id: number; channel: NonNullable<Fan['notify_channel']> }) =>
      api.put(`/api/channel/${id}`, { channel }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['fans'] }),
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-slate-800">粉丝</h2>
        <input
          className="input max-w-xs"
          placeholder="昵称 / OpenID 搜索"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>
      {error && <div className="rounded bg-red-50 px-3 py-2 text-sm text-red-700">{(error as Error).message}</div>}
      <div className="card overflow-hidden p-0">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
            <tr>
              <th className="px-4 py-2">昵称</th>
              <th className="px-4 py-2">OpenID</th>
              <th className="px-4 py-2">通道</th>
              <th className="px-4 py-2">订阅</th>
              <th className="px-4 py-2">最近活跃</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {isLoading && (
              <tr><td className="px-4 py-6 text-center text-slate-400" colSpan={5}>加载中...</td></tr>
            )}
            {!isLoading && (data?.items ?? []).length === 0 && (
              <tr><td className="px-4 py-6 text-center text-slate-400" colSpan={5}>暂无粉丝</td></tr>
            )}
            {(data?.items ?? []).map((f) => (
              <tr key={f.id}>
                <td className="px-4 py-2 text-slate-800">{f.nickname || '-'}</td>
                <td className="px-4 py-2 font-mono text-xs text-slate-500">{f.openid}</td>
                <td className="px-4 py-2">
                  <select
                    className="rounded border border-slate-300 bg-white px-2 py-1 text-xs"
                    value={f.notify_channel ?? ''}
                    onChange={(e) => setChannel.mutate({ id: f.id, channel: e.target.value as NonNullable<Fan['notify_channel']> })}
                  >
                    <option value="">未设置</option>
                    {CHANNELS.map((c) => <option key={c} value={c!}>{c}</option>)}
                  </select>
                </td>
                <td className="px-4 py-2">{f.subscribed ? '是' : '否'}</td>
                <td className="px-4 py-2 text-slate-500">{f.last_active_at ?? '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
